<div class="rule-editor" [formGroup]="form">
  <!-- Badge -->
  <div class="rule-editor__badge">
    @if (badgeIconUrl) {
      <img [src]="badgeIconUrl" class="rule-type-picker__logo" [alt]="headerLabel" />
    } @else {
      <mat-icon>{{ badgeIcon }}</mat-icon>
    }
  </div>

  <!-- Body -->
  <div class="rule-editor__body">
    @if (operatorFirst) {
      <ng-container [ngTemplateOutlet]="comparisonSelect"></ng-container>
    }

    <!-- Header Type Label — a button opening the shared picker when provided so
         changing a criterion's type uses the same searchable picker as adding. -->
    @if (sharedTypeMenu && !readOnly) {
      <button
        type="button"
        class="rule-editor__type-btn"
        [matMenuTriggerFor]="sharedTypeMenu"
        (click)="onTypeBtnClick()"
        (menuOpened)="typeMenuOpened.emit()"
        data-test="inline-editor-type">
        <span class="rule-editor__type-btn-label">{{ headerLabel }}</span>
        <mat-icon class="rule-editor__type-btn-arrow">arrow_drop_down</mat-icon>
      </button>
    } @else {
      <span class="rule-editor__type-text">{{ headerLabel }}</span>
    }

    <!-- Loading spinner for options -->
    @if (loadingOptions) {
      <mat-spinner diameter="18" class="inline-spinner"></mat-spinner>
    } @else {
      <!-- Operator Select (for non-campaign conditions) -->
      @if (showOperator && !isCampaignCondition && !operatorFirst) {
        @if (comparisonOptions.length) {
          <ng-container [ngTemplateOutlet]="comparisonSelect"></ng-container>
        } @else {
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
            <mat-label>{{ "data-platform.operator" | translate }}</mat-label>
            <mat-select formControlName="operator">
              @for (op of supportedOperators; track op) {
                <mat-option [value]="op">{{ getOperatorLabel(op) | translate }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      }

      <!-- VALUE INPUTS BASED ON INPUT TYPE -->

      <!-- Boolean Dropdown (Yes/No) -->
      @if (filterInputType === InputTypes.BooleanDropdown) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
          <mat-select formControlName="value">
            <mat-option value="true">{{ "generic.yes" | translate }}</mat-option>
            <mat-option value="false">{{ "generic.no" | translate }}</mat-option>
          </mat-select>
        </mat-form-field>
      }

      <!-- TextField / Number (Simple) -->
      @if (filterInputType === InputTypes.TextField || filterInputType === InputTypes.Number) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
          <input
            matInput
            [type]="filterInputType === InputTypes.Number ? 'number' : 'text'"
            formControlName="value"
            [placeholder]="placeholderText | translate" />
          @if (minimumValue !== undefined) {
            <mat-error data-test="inline-editor-minimum-error">
              {{ "audience.session-condition-groups.returning-visitor-less-than-min" | translate: { min: minimumValue } }}
            </mat-error>
          }
        </mat-form-field>
      }

      <!-- Single Choice Dropdown (Simple, not Campaign condition) -->
      @if (filterInputType === InputTypes.SingleChoiceDropdown && !isCampaignCondition) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
          <mat-select formControlName="value" [placeholder]="'generic.choose' | translate" (selectionChange)="onFilterValueSelected($event.value)">
            @for (opt of choosableValueOptions; track opt.key) {
              <mat-option [value]="opt.key">{{ opt.value | translate }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        <!-- AppTags sub selection -->
        @if (isAppTags) {
          @if (choosableValueTagOptions.length > 0) {
            <span class="rule-editor__sub-text">
              {{ "audience.filter-config." + $safeNavigationMigration(filter?.filterConfig?.subDescriptionKey) | translate }}:
            </span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
              <mat-select formControlName="filterValueTagString" [placeholder]="'generic.choose' | translate">
                @for (opt of choosableValueTagOptions; track opt.key) {
                  <mat-option [value]="opt.key">{{ opt.value | translate }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          } @else if (form.get("value")?.value) {
            <span class="rule-editor__muted-info">{{ "audience.filter-config.filter-list-have-no-tags" | translate }}</span>
          }
        }
      }

      <!-- Campaign selection — searchable widget/campaign selector menu. -->
      @if (filterInputType === InputTypes.SingleChoiceDropdown && isCampaignCondition) {
        <app-widget-selector-menu
          class="rule-editor__field"
          [items]="allWidgets"
          [idSource]="'campaign'"
          [multiple]="false"
          formControlName="value"
          [placeholder]="'generic.choose' | translate"></app-widget-selector-menu>
      }

      <!-- Multi Choice Dropdown -->
      @if (filterInputType === InputTypes.MultiChoiceDropdown) {
        @if (isCountryFilter) {
          <app-country-select
            [value]="form.get('valueArray')?.value"
            (valueChange)="form.get('valueArray')?.setValue($event)"
            [required]="true"
            class="rule-editor__field"></app-country-select>
        } @else {
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field">
            <mat-select formControlName="valueArray" multiple [placeholder]="'generic.choose' | translate">
              @for (opt of choosableValueOptions; track opt.key) {
                <mat-option [value]="opt.key">{{ opt.value }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      }

      <!-- Javascript Text Area -->
      @if (filterInputType === InputTypes.JavascriptTextArea) {
        <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__field rule-editor__field--textarea">
          <textarea matInput rows="2" formControlName="value" placeholder="e.g. return session.visits > 2;"></textarea>
        </mat-form-field>
      }

      <!-- Campaign condition specific fields (CampaignOpened, etc.) -->
      @if (isCampaignCondition) {
        <div class="inline-sub-row">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
            <mat-select formControlName="campaignEventOperator">
              @for (op of CampaignOperators; track op) {
                <mat-option [value]="op">{{ "audience.session-condition-groups.campaignEventCountOperators." + op | translate }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
            <input matInput type="number" formControlName="campaignEventCount" min="0" max="10" />
          </mat-form-field>
          <span class="rule-editor__muted-info">{{ "generic.times" | translate | lowercase }}</span>
        </div>
      }

      <!-- COMPLEX WEATHER & TEMPERATURE DETAILS -->
      @if (filter?.type === FilterTypes.Weather || filter?.type === FilterTypes.Temperature) {
        <div class="inline-sub-row inline-sub-row--weather">
          <!-- WeatherType Toggle Group -->
          <mat-button-toggle-group formControlName="weatherType" hideSingleSelectionIndicator="true" class="weather-buttons-inline">
            @for (type of WeatherTypeOptions; track type) {
              <mat-button-toggle [value]="type">
                <span class="weather-btn-content">
                  <img [src]="'/assets/img/icons/weather/' + type.toLowerCase() + '.svg'" alt="" class="weather-btn-icon" />
                  <span>{{ "audience.filter-config.weatherType-" + type.toLowerCase() | translate }}</span>
                </span>
              </mat-button-toggle>
            }
          </mat-button-toggle-group>

          <!-- Latitude / Longitude -->
          <div class="weather-coord">
            <span class="weather-row-title">
              <span>{{ "audience.filter-config.weatherLocation-title" | translate }}</span>
              <span class="weather-optional">({{ "generic.optional" | translate }})</span>
              :
            </span>
            <span class="weather-coord-label">{{ "audience.filter-config.longitude" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="longitude" />
            </mat-form-field>
            <span class="weather-coord-label">{{ "audience.filter-config.latitude" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="latitude" />
            </mat-form-field>
          </div>

          <!-- Temp limits -->
          <div class="weather-limits">
            <span class="weather-row-title">
              <span>{{ "audience.filter-config.weatherTemperature-title" | translate }}</span>
              <span class="weather-optional">({{ "generic.optional" | translate }})</span>
              :
            </span>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherTemperature-before" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="minTemp" />
            </mat-form-field>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherTemperature-between" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="maxTemp" />
            </mat-form-field>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherTemperature-after" | translate }}</span>
          </div>

          <!-- Wind limits -->
          <div class="weather-limits">
            <span class="weather-row-title">
              <span>{{ "audience.filter-config.weatherWind-title" | translate }}</span>
              <span class="weather-optional">({{ "generic.optional" | translate }})</span>
              :
            </span>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherTemperature-before" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="minWind" min="0" />
            </mat-form-field>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherTemperature-between" | translate }}</span>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
              <input matInput type="number" formControlName="maxWind" min="0" />
            </mat-form-field>
            <span class="weather-coord-label">{{ "audience.filter-config.weatherWind-after" | translate }}</span>
          </div>

          <!-- Timeframe selector -->
          <div class="weather-timeframe">
            <span class="weather-coord-label">{{ "dashboard.leads.when" | translate }}:</span>
            <mat-select
              formControlName="weatherTimeframe"
              class="rule-editor__op"
              style="background: var(--bgCard, #fff); border: 1px solid var(--inputFieldLine); border-radius: 4px; padding: 4px 8px; font-size: 13px">
              <mat-option value="now">{{ "audience.filter-config.weather-choice-now" | translate }}</mat-option>
              <mat-option value="checkToday">{{ "audience.filter-config.weather-today" | translate }}</mat-option>
              <mat-option value="checkTomorrow">{{ "audience.filter-config.weather-tomorrow" | translate }}</mat-option>
              <mat-option value="daysForward">{{ "audience.filter-config.weather-days-forward" | translate }}</mat-option>
              <mat-option value="specificDay">{{ "audience.filter-config.weather-specific-date" | translate }}</mat-option>
              <mat-option value="checkNextWeekend">{{ "audience.filter-config.weather-next-weekend" | translate }}</mat-option>
              <mat-option value="inDays">{{ "audience.filter-config.weather-in-days" | translate }}</mat-option>
            </mat-select>

            <!-- Conditional sub inputs -->
            @if (form.get("weatherTimeframe")?.value === "daysForward") {
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
                <input matInput type="number" formControlName="weatherDaysForward" min="1" max="10" />
              </mat-form-field>
            }
            @if (form.get("weatherTimeframe")?.value === "inDays") {
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
                <input matInput type="number" formControlName="weatherInDays" min="1" max="10" />
              </mat-form-field>
            }
            @if (form.get("weatherTimeframe")?.value === "specificDay") {
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
                <input matInput [matDatepicker]="weatherPicker" formControlName="weatherSpecificDay" />
                <mat-datepicker-toggle matSuffix [for]="weatherPicker"></mat-datepicker-toggle>
                <mat-datepicker #weatherPicker></mat-datepicker>
              </mat-form-field>
            }
          </div>
        </div>
      }

      <!-- COMPLEX GOALS & PURCHASE DETAILS -->
      @if (filter?.type === FilterTypes.Goals || filter?.type === FilterTypes.Purchase) {
        <div class="inline-sub-row">
          <!-- Frequency -->
          <span class="rule-editor__sub-text">{{ "audience.session-condition-groups.conditionType.Goals" | translate }}</span>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
            <mat-select formControlName="frequencyType">
              @for (op of FreqOperators; track op) {
                <mat-option [value]="op">{{ getOperatorLabel(op) | translate }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
            <input matInput type="number" formControlName="frequencyValue" min="0" />
          </mat-form-field>
          <span class="rule-editor__sub-text">{{ "generic.times" | translate | lowercase }}</span>

          <!-- Timeframe -->
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
            <mat-label>{{ "data-platform.timeframe" | translate }}</mat-label>
            <mat-select formControlName="timeOperator">
              @for (tf of Timeframes; track tf) {
                <mat-option [value]="tf">{{ getOperatorLabel(tf) | translate }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (form.get("timeOperator")?.value === "TimeWindow") {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
              <mat-label>{{ "data-platform.days-ago" | translate }}</mat-label>
              <input matInput type="number" formControlName="days" min="1" />
              <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
            </mat-form-field>
          }

          @if (form.get("timeOperator")?.value === "SinceDate" || form.get("timeOperator")?.value === "BetweenDates") {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
              <mat-label>{{ "data-platform.tf-start-date" | translate }}</mat-label>
              <input matInput [matDatepicker]="startPicker" formControlName="startDate" />
              <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
              <span matTextSuffix><mat-datepicker #startPicker></mat-datepicker></span>
            </mat-form-field>
          }

          @if (form.get("timeOperator")?.value === "BetweenDates") {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
              <mat-label>{{ "data-platform.tf-end-date" | translate }}</mat-label>
              <input matInput [matDatepicker]="endPicker" formControlName="endDate" [min]="form.get('startDate')?.value" />
              <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
              <span matTextSuffix><mat-datepicker #endPicker></mat-datepicker></span>
            </mat-form-field>
          }

          @if (form.get("timeOperator")?.value === "BetweenDaysAgo") {
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
              <mat-label>{{ "data-platform.tf-days-ago-start" | translate }}</mat-label>
              <input matInput type="number" formControlName="days" min="1" />
              <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
            </mat-form-field>
            <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__days">
              <mat-label>{{ "data-platform.tf-days-ago-end" | translate }}</mat-label>
              <input matInput type="number" formControlName="daysUpper" [min]="form.get('days')?.value || 1" />
              <span matTextSuffix>{{ "data-platform.days" | translate }}</span>
            </mat-form-field>
          }

          <!-- Sales Details for Purchase -->
          @if (filter?.type === FilterTypes.Purchase) {
            <div class="sales-inline-section">
              <span class="rule-editor__sub-text">{{ "audience.purchase" | translate }}</span>
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
                <mat-select formControlName="salesOperator">
                  @for (op of SalesOperators; track op) {
                    <mat-option [value]="op">{{ "audience.salesOperators." + op | translate }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>

              @if (form.get("salesOperator")?.value !== "Any") {
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
                  <input matInput type="number" formControlName="salesValue" min="0" />
                </mat-form-field>
              }

              @if (form.get("salesOperator")?.value === "Between") {
                <span class="rule-editor__sub-text">&nbsp;{{ "generic.and" | translate }}&nbsp;</span>
                <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__value">
                  <input matInput type="number" formControlName="salesUpperValue" min="0" />
                </mat-form-field>
              }

              <!-- Coupon Toggle -->
              <mat-slide-toggle formControlName="appliedCoupon" color="primary" class="rule-editor__toggle">
                {{ "audience.applied-coupon" | translate }}
              </mat-slide-toggle>
            </div>
          }
        </div>
      }
    }
  </div>

  <!-- Remove Button -->
  <button mat-icon-button type="button" class="rule-editor__remove" (click)="onRemove()" [attr.aria-label]="'audience.remove-filter' | translate">
    <mat-icon>close</mat-icon>
  </button>

  <ng-template #comparisonSelect>
    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="rule-editor__op">
      <mat-label>{{ "data-platform.operator" | translate }}</mat-label>
      <mat-select formControlName="comparison" data-test="inline-editor-comparison">
        @for (option of comparisonOptions; track option.value) {
          <mat-option [value]="option.value">{{ option.labelKey | translate }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </ng-template>
</div>
